import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    SimpleChanges,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexNonAxisChartSeries,
    ApexPlotOptions,
    ApexStroke,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    ChartType,
    ApexGrid,
    ApexLegend,
    ApexAnnotations,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorAec9Cream,
} from '../../all.constants';
import { convertNumber } from '../../all.helpers';

import frLocale from 'apexcharts/dist/locales/fr.json';

@Component({
    selector: 'chart-card',
    templateUrl: './chart-card.component.html',
    styleUrls: ['./chart-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartCardComponent {
    @Input() title: string = '';
    @Input() descriptionTitle: string | undefined;
    @Input() descriptionContent: string | undefined;
    @Input() error: boolean = false;
    @Input() isLoading: boolean = true;
    @Input() colors: string[] | undefined;
    @Input() unitTooltip: string = '';
    @Input() annotations: ApexAnnotations | undefined;

    private style = { fontWeight: 600, fontStyle: '12px' };

    @Input() set xaxisLabels(labels: ApexXAxis['labels']) {
        this.updateXaxis({ labels: { ...labels, hideOverlappingLabels: true } });
    }

    @Input() set xaxisCategories(categories: string[] | number[]) {
        this.updateXaxis({ categories: categories });
    }

    @Input() set xaxisType(value: 'datetime' | 'category' | 'numeric') {
        if (value) {
            this.updateXaxis({ type: value });
        }
    }

    @Input() set xaxisTitle(value: string | undefined) {
        if (value) {
            this.updateXaxis({
                title: { text: value, style: this.style },
            });
        }
    }

    @Input() set xaxisTickAmount(value: number | undefined) {
        this._xaxisTickAmount = value;
        this.updateXaxis();
    }

    @Input() set xmin(value: number | undefined) {
        this.updateXaxis({ min: value });
    }

    @Input() set xmax(value: number | undefined) {
        this.updateXaxis({ max: value });
    }

    @Input() legend: ApexLegend = {
        showForSingleSeries: true,
    };
    @Input() public yaxisPrecision: number = 0;
    @Input() public set yaxis(value: ApexYAxis[]) {
        this._yaxis = value?.map((y) => {
            if (y.title?.text) {
                y.title.style = this.style;
            }
            return {
                ...y,
                labels: {
                    formatter: (value: any) => {
                        if (typeof value === 'number') {
                            return convertNumber(
                                value,
                                this.yaxisPrecision || this.precision,
                                false,
                            );
                        }
                        return value;
                    },
                    ...y.labels,
                },
            };
        });
    }
    public _yaxis: ApexYAxis[] = [];

    @Input() public set series(values: ApexAxisChartSeries | ApexNonAxisChartSeries) {
        this._series = values;
    }

    public _series: ApexAxisChartSeries | ApexNonAxisChartSeries = [];

    @Input() public grid: ApexGrid = {};

    public _dashArray: number[] | undefined;

    @Input() public set dashArray(value: number[] | undefined) {
        if (value) {
            this.updateStroke(value);
        }
    }

    //Number of decimal to show
    @Input() public precision: number | null = null;

    @Input() public set dataLabels(dataLabels: ApexDataLabels) {
        if (!dataLabels.formatter) {
            dataLabels.formatter = (v: any) => convertNumber(v, this.precision, false);
        }
        this._dataLabels = dataLabels;
    }
    public _dataLabels: ApexDataLabels = { enabled: true };

    @Input() labels: string[] = [];
    @Input() set type(value: ChartType) {
        this.chart.type = value || 'line';
    }

    @Input() set stacked(value: boolean) {
        this.chart.stacked = value || false;
    }

    @Input() set sharedTooltip(value: boolean) {
        if (value) {
            this.updateTooltipShared(value);
        }
    }

    public _plotOptions: ApexPlotOptions = {
        bar: {
            columnWidth: '70%',
            horizontal: false,

            dataLabels: {
                hideOverflowingLabels: true,
                position: 'center',
            },
        },
        boxPlot: {
            colors: {
                upper: colorAec1BlueLight,
                lower: colorAec2BlueDark,
            },
        },
    };

    @Input() set plotOptions(plotOptions: Partial<ApexPlotOptions>) {
        this._plotOptions = {
            ...this._plotOptions,
            ...plotOptions,
            bar: {
                ...this._plotOptions.bar,
                ...plotOptions?.bar,
                dataLabels: {
                    ...this._plotOptions.bar?.dataLabels,
                    ...plotOptions?.bar?.dataLabels,
                },
            },
            boxPlot: {
                ...this._plotOptions.boxPlot,
                ...plotOptions?.boxPlot,
                colors: {
                    ...this._plotOptions.boxPlot?.colors,
                    ...plotOptions?.boxPlot?.colors,
                },
            },
        };
    }

    @Input() fill: ApexFill = {
        opacity: 0.4,
    };

    @Input() set customTooltip(formatter: ApexTooltip['custom']) {
        if (formatter) {
            this.updateTooltip(formatter);
        }
    }

    @Input() set id(value: string) {
        this.chart.id = value;
    }

    @Input() set height(value: number) {
        this.chart.height = value;
    }

    @Input() cardClass: string = '';

    // Bind the cardClass property to the class attribute of the host element
    @HostBinding('class') get hostClasses(): string {
        return this.cardClass;
    }

    public xaxis: ApexXAxis = {
        type: 'category',
        labels: {
            hideOverlappingLabels: true,
        },
        title: { text: '', style: this.style },
    };

    private _xaxisTickAmount?: number;

    public chart: ApexChart = {
        id: 'myChart',
        locales: [frLocale],
        defaultLocale: 'fr',
        type: 'line',
        height: 300,
        stacked: false,
        zoom: {
            enabled: false,
        },
        toolbar: {
            show: true,
            tools: {
                download: '<i class="icon icon-download" style="font-size:50%"></i>',
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false,
            },
            export: {
                csv: {
                    filename: undefined,
                    columnDelimiter: ',',
                },
                png: {
                    filename: undefined,
                },
            },
        },
    };

    public stroke: ApexStroke = {
        width: 2,
        curve: 'smooth',
    };

    private updateStroke(dashArray: number[]) {
        this.stroke = {
            ...this.stroke,
            dashArray: dashArray,
        };
    }

    private updateTooltipShared(sharedValue: boolean) {
        this.tooltip = {
            ...this.tooltip,
            shared: sharedValue,
            intersect: false,
        };
    }

    public tooltip: ApexTooltip = {
        y: {
            formatter: (value: number) => {
                const formattedValue = convertNumber(value, this.precision, false);
                return this.unitTooltip ? `${formattedValue} ${this.unitTooltip}` : formattedValue;
            },
        },
    };

    public defaultColors: string[] = [
        colorAec1BlueLight,
        colorAec2BlueDark,
        colorAec3Green,
        colorAec4Orange,
        colorAec5Brown,
        colorAec6Gray,
        colorAec7RedLight,
        colorAec8RedDark,
        colorAec9Cream,
    ];

    constructor(private datePipe: DatePipe) {}

    ngOnInit() {
        const date = new Date();
        const fileName = `${this.title.replace(' ', '_')}_${this.datePipe.transform(date, "dd.MM.yyyy_HH'h'mm")}`;
        this.chart!.toolbar!.export!.csv!.filename = fileName;
        this.chart!.toolbar!.export!.png!.filename = fileName;
    }

    private updateXaxis(customProps?: Partial<ApexXAxis>): void {
        this.xaxis = {
            ...this.xaxis,
            ...customProps,
            tickAmount: this._xaxisTickAmount ?? undefined,
            labels: {
                ...this.xaxis?.labels,
                ...customProps?.labels,
                hideOverlappingLabels: true,
            },
        };
    }

    private updateTooltip(formatter: ApexTooltip['custom']): void {
        this.tooltip = {
            ...this.tooltip,
            custom: formatter,
        };
    }
}
